/**
 * Pure mermaid config utilities — shared between server (tools/mermaid.ts)
 * and client (mermaid-renderer.tsx). No 'use client' directive here.
 */

/**
 * Extract YAML frontmatter config from mermaid code.
 */
export function extractFrontmatter(code: string): { config: Record<string, unknown> | null; diagramCode: string } {
  const trimmed = code.trim();
  if (!trimmed.startsWith('---')) {
    return { config: null, diagramCode: trimmed };
  }

  const lines = trimmed.split(/\r?\n/);
  let closeLineIndex = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '---') {
      closeLineIndex = i;
      break;
    }
  }

  if (closeLineIndex === -1) {
    return { config: null, diagramCode: trimmed };
  }

  const yamlLines = lines.slice(1, closeLineIndex);
  const diagramCode = lines.slice(closeLineIndex + 1).join('\n').trim();

  try {
    const config: Record<string, unknown> = {};
    const themeVariables: Record<string, string> = {};
    let inThemeVariables = false;

    for (const line of yamlLines) {
      const stripped = line.trim();
      if (!stripped || stripped.startsWith('#')) continue;

      if (stripped === 'themeVariables:') {
        inThemeVariables = true;
        continue;
      }

      if (inThemeVariables && line.match(/^\s{4,}/)) {
        const match = stripped.match(/^(\w+):\s*["']?([^"']*)["']?$/);
        if (match) {
          themeVariables[match[1]] = match[2];
        }
        continue;
      }

      if (inThemeVariables && !line.match(/^\s{4,}/)) {
        inThemeVariables = false;
      }

      const kvMatch = stripped.match(/^(\w+):\s*["']?([^"']*)["']?$/);
      if (kvMatch && kvMatch[2]) {
        config[kvMatch[1]] = kvMatch[2];
      }
    }

    if (Object.keys(themeVariables).length > 0) {
      config.themeVariables = themeVariables;
    }

    return { config: Object.keys(config).length > 0 ? config : null, diagramCode };
  } catch {
    return { config: null, diagramCode };
  }
}

/**
 * Build mermaid initialization config.
 * No hardcoded defaults — the LLM has full control via frontmatter.
 */
type MermaidTheme = 'default' | 'base' | 'dark' | 'forest' | 'neutral' | 'null';

export function buildMermaidConfig(frontmatterConfig?: Record<string, unknown> | null) {
  const validThemes: MermaidTheme[] = ['default', 'base', 'dark', 'forest', 'neutral', 'null'];
  let theme: MermaidTheme = 'default';
  let themeVariables: Record<string, string> | undefined;

  if (frontmatterConfig) {
    const fmConfig = (frontmatterConfig.config ?? frontmatterConfig) as Record<string, unknown>;

    if (typeof fmConfig.theme === 'string' && validThemes.includes(fmConfig.theme as MermaidTheme)) {
      theme = fmConfig.theme as MermaidTheme;
    }

    if (fmConfig.themeVariables && typeof fmConfig.themeVariables === 'object') {
      themeVariables = fmConfig.themeVariables as Record<string, string>;
      if (theme === 'default') theme = 'base';
    }
  }

  return {
    startOnLoad: false,
    theme,
    securityLevel: 'loose' as const,
    fontFamily: 'system-ui, -apple-system, sans-serif',
    arrowMarkerAbsolute: false,
    ...(themeVariables ? { themeVariables } : {}),
  };
}
